import { create } from 'zustand';

import { getLocalDate } from '../utils/dateUtils';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';

export interface DailyMood {
  id: string;
  emoji: string;
  note?: string;
  date: string;
  created_at: string;
}

interface DailyMoodState {
  moods: DailyMood[];
  loading: boolean;
  fetchMoods: (widgetId: string) => Promise<void>;
  setMood: (widgetId: string, emoji: string, note?: string) => Promise<void>;
  updateMood: (id: string, emoji: string, note?: string) => Promise<void>;
}

export const useDailyMoodStore = create<DailyMoodState>((set, get) => ({
  moods: [],
  loading: false,

  fetchMoods: async (widgetId: string) => {
    set({ loading: true });
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('daily_moods')
          .select('*')
          .eq('user_id', userId)
          .eq('widget_id', widgetId)
          .order('date', { ascending: false });

        if (error) throw error;
        set({ moods: data || [] });
      });
    } catch (error) {
      handleException(error);
    } finally {
      set({ loading: false });
    }
  },

  setMood: async (widgetId: string, emoji: string, note?: string) => {
    const today = getLocalDate();
    const optimisticMood: DailyMood = {
      id: crypto.randomUUID(),
      emoji,
      note,
      date: today,
      created_at: new Date().toISOString()
    };

    set((state) => ({
      moods: [optimisticMood, ...state.moods.filter(m => m.date !== today)]
    }));

    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('daily_moods')
          .upsert(
            {
              user_id: userId,
              widget_id: widgetId,
              emoji,
              note,
              date: today
            },
            {
              onConflict: 'user_id,date',
              ignoreDuplicates: false
            }
          )
          .select()
          .single();

        if (error) throw error;
        if (data) {
          set((state) => ({
            moods: [
              data,
              ...state.moods.filter(m => m.id !== optimisticMood.id && m.date !== today)
            ]
          }));
        }
      });
    } catch (error) {
      set((state) => ({
        moods: state.moods.filter(m => m.id !== optimisticMood.id)
      }));
      handleException(error);
    }
  },

  updateMood: async (id: string, emoji: string, note?: string) => {
    const previousMoods = get().moods;

    set((state) => ({
      moods: state.moods.map(mood =>
        mood.id === id ? { ...mood, emoji, note } : mood
      )
    }));

    try {
      const { error } = await supabase
        .from('daily_moods')
        .update({ emoji, note })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      set({ moods: previousMoods });
      handleException(error);
    }
  }
}));
